import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { useSubsidyCalculator } from '../hooks/useSubsidyCalculator';
import { useRequestSubsidy } from '../hooks/useRequestSubsidy';
import { GET_SUBSIDY_CONTEXT, GET_SUBSIDY_REQUESTS, SubsidyRequestExtended } from '../api';
import { SubsidyTable } from './subsidyTable';
import { SubsidyCertificateDialog } from './SubsidyCertificateDialog';
import { SubsidyEquipment } from '../types';

interface Props {
  accountId: string;
  creditSubmissionId: string;
  equipments: SubsidyEquipment[];
  isEquipmentLoading: boolean;
}

export const SubsidyCalculatorPanel = ({ accountId, creditSubmissionId, equipments, isEquipmentLoading }: Props) => {
  const {
    selectedProgramId,
    programOptions,
    isOptionsLoading,
    isProgramLoading,
    program,
    isSubsidyEligible,
    subsidyPercent,
    dnetPercent,
    termOptions,
    selectedTerm,
    handleTermChange,
    handleProgramChange,
  } = useSubsidyCalculator({ accountId });

  const { requestSubsidy, submittedEquipmentIds, isRequesting } = useRequestSubsidy();
  const { handleOpen: openCertificate } = useModal(ModalsKeys.SubsidyCertificateDialog);

  const { data: subsidyContextData } = useQuery<{
    subsidyContext: { dealerParticipationPercentage: number };
  }>(GET_SUBSIDY_CONTEXT, {
    variables: { input: { creditSubmissionId } },
    skip: !creditSubmissionId,
    fetchPolicy: 'cache-and-network',
  });

  const dealerParticipationPercent = subsidyContextData?.subsidyContext?.dealerParticipationPercentage ?? 0;

  const {
    data: subsidyRequestsData,
    loading: isSubsidyRequestsLoading,
    refetch: refetchSubsidyRequests,
  } = useQuery<{
    subsidyRequests: SubsidyRequestExtended[];
  }>(GET_SUBSIDY_REQUESTS, {
    variables: { creditSubmissionId, financeProgramId: selectedProgramId },
    skip: !selectedProgramId,
    fetchPolicy: 'cache-and-network',
  });

  const existingEquipmentIds = (subsidyRequestsData?.subsidyRequests ?? [])
    .map(r => r.equipment?.id)
    .filter((id): id is string => !!id);

  const allSubmittedEquipmentIds = Array.from(new Set([...existingEquipmentIds, ...submittedEquipmentIds]));

  if (isEquipmentLoading) {
    return (
      <CardContainer title="Subsidy Calculator">
        <Stack alignItems="center" justifyContent="center" py="2rem">
          <CircularProgress />
        </Stack>
      </CardContainer>
    );
  }

  if (!equipments.length) {
    return (
      <CardContainer title="Subsidy Calculator">
        <Alert severity="info">
          Subsidy cannot be calculated because there is no equipment associated to this deal.
        </Alert>
      </CardContainer>
    );
  }

  return (
    <>
      <SubsidyCertificateDialog />
      <CardContainer title="Subsidy Calculator">
        <Stack gap="1.5rem">
          <FormControl size="small" fullWidth variant="standard" disabled={isOptionsLoading}>
            <InputLabel>Finance Program</InputLabel>
            <Select
              label="Finance Program"
              value={selectedProgramId}
              onChange={e => handleProgramChange(e.target.value)}
              endAdornment={isOptionsLoading ? <CircularProgress size={16} sx={{ mr: 3 }} /> : null}
            >
              {programOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isProgramLoading && (
            <Stack alignItems="center" justifyContent="center" py="2rem">
              <CircularProgress />
            </Stack>
          )}

          {!isProgramLoading && program && !isSubsidyEligible && (
            <Alert severity="error">This program is not eligible for subsidy</Alert>
          )}

          {!isProgramLoading && program && isSubsidyEligible && !termOptions.length && (
            <Alert severity="warning">
              This subsidy program has no terms configured. Please update the finance program.
            </Alert>
          )}

          {!isProgramLoading && program && isSubsidyEligible && !!termOptions.length && (
            <FormControl size="small" fullWidth variant="standard">
              <InputLabel>Term</InputLabel>
              <Select label="Term" value={selectedTerm} onChange={e => handleTermChange(e.target.value)}>
                {termOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!isProgramLoading && program && isSubsidyEligible && !!termOptions.length && selectedTerm && (
            <Stack gap="1rem">
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Stack gap="0.25rem">
                  <Typography variant="caption" color="text.secondary">
                    Subsidy %
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {subsidyPercent}%
                  </Typography>
                </Stack>

                <Stack gap="0.25rem">
                  <Typography variant="caption" color="text.secondary">
                    DNET %
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {dnetPercent}%
                  </Typography>
                </Stack>
              </Box>

              {isSubsidyRequestsLoading ? (
                <Stack alignItems="center" justifyContent="center" py="1rem">
                  <CircularProgress size={24} />
                </Stack>
              ) : (
                equipments.map(equipment => {
                  const label =
                    [equipment.year, equipment.make, equipment.model, equipment.serialNumberVin || null]
                      .filter(Boolean)
                      .join(' ') || equipment.equipmentId;

                  const existingRequest = (subsidyRequestsData?.subsidyRequests ?? []).find(
                    r => r.equipment?.id === equipment.equipmentId
                  );

                  return (
                    <Stack key={equipment.equipmentId} gap="0.5rem">
                      <Typography variant="subtitle2">{label}</Typography>
                      <SubsidyTable
                        subsidyPercent={existingRequest ? existingRequest.subsidyPercentage : subsidyPercent}
                        dnetCoefficient={existingRequest ? existingRequest.dnetPercentage / 100 : dnetPercent / 100}
                        dealerParticipationPercent={
                          existingRequest
                            ? (existingRequest.dealerParticipationPercentage ?? dealerParticipationPercent)
                            : dealerParticipationPercent
                        }
                        isSubmitted={allSubmittedEquipmentIds.includes(equipment.equipmentId)}
                        isRequesting={isRequesting}
                        existingValues={
                          existingRequest
                            ? {
                                dnet: existingRequest.dnet,
                                financeAmount: existingRequest.financeAmount,
                                approvedSubsidyAmount: existingRequest.approvedSubsidyAmount,
                                dealerContribution: existingRequest.dealerContribution ?? 0,
                                oemContribution: existingRequest.oemContribution ?? 0,
                              }
                            : undefined
                        }
                        onRequestSubsidy={tableRow =>
                          requestSubsidy({
                            creditSubmissionId,
                            financeProgramId: selectedProgramId,
                            equipmentId: equipment.equipmentId,
                            term: Number(selectedTerm),
                            subsidyPercentage: subsidyPercent,
                            dnetPercentage: dnetPercent,
                            dealerParticipationPercentage: dealerParticipationPercent,
                            tableRow,
                            onSuccess: refetchSubsidyRequests,
                          })
                        }
                        onShowCertificate={
                          existingRequest
                            ? () =>
                                openCertificate({
                                  ...existingRequest,
                                  equipment: existingRequest.equipment ?? null,
                                  dealerParticipationPercentage:
                                    existingRequest.dealerParticipationPercentage ?? undefined,
                                  dealerContribution: existingRequest.dealerContribution ?? undefined,
                                  oemContribution: existingRequest.oemContribution ?? undefined,
                                })
                            : undefined
                        }
                      />
                    </Stack>
                  );
                })
              )}
            </Stack>
          )}
        </Stack>
      </CardContainer>
    </>
  );
};
